/**
 * Contenu de secours du hero de la vitrine (US — rendu initial riche AVANT toute
 * saisie admin). Quand le resto n'a pas encore renseigné son accroche / sous-titre,
 * on génère un texte attractif dérivé du nom et des catégories du menu — au lieu
 * d'afficher seulement le nom du resto sur un fond noir.
 *
 * Fonctions PURES (zéro dépendance) → testées unitairement.
 * Règle d'or : aucune valeur métier en dur ; tout est dérivé des données du tenant.
 */

/** Accroche par défaut si `tenant.tagline` est vide. Ex. « Votre commande Big Burger prête à emporter. » */
export function defaultTagline(name: string | null | undefined): string {
  const n = (name ?? '').trim();
  if (!n) return 'Votre commande prête à emporter.';
  return `Votre commande ${n} prête à emporter.`;
}

/**
 * Sous-titre par défaut dérivé des catégories du menu (3 max, dédoublonnées).
 * Ex. ["Burgers","Tacos","Paninis"] → « Burgers, tacos et paninis à commander en ligne, retrait rapide sur place. »
 * Repli générique si aucune catégorie.
 */
export function defaultPitch(categoryNames: readonly (string | null | undefined)[]): string {
  const top = uniqueTop(categoryNames, 3).map((c) => c.toLowerCase());
  if (top.length === 0) {
    return 'Commandez en ligne, retrait rapide sur place.';
  }
  const sentence = `${humanList(top)} à commander en ligne, retrait rapide sur place.`;
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
}

/** Accroche effective : la saisie admin si présente, sinon le repli généré. */
export function heroTitle(tagline: string | null | undefined, name: string): string {
  const t = (tagline ?? '').trim();
  return t || defaultTagline(name);
}

/** Sous-titre effectif : la saisie admin si présente, sinon le repli dérivé des catégories. */
export function heroSubtitle(
  shortPitch: string | null | undefined,
  categoryNames: readonly (string | null | undefined)[],
): string {
  const p = (shortPitch ?? '').trim();
  return p || defaultPitch(categoryNames);
}

/** N premières valeurs non vides, sans doublon (insensible à la casse), ordre conservé. */
function uniqueTop(items: readonly (string | null | undefined)[], n: number): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of items) {
    const v = (raw ?? '').trim();
    if (!v) continue;
    const key = v.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(v);
    if (out.length >= n) break;
  }
  return out;
}

/** Énumération naturelle : ["a"] → "a" ; ["a","b"] → "a et b" ; ["a","b","c"] → "a, b et c". */
function humanList(items: readonly string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  return `${items.slice(0, -1).join(', ')} et ${items[items.length - 1]}`;
}
